/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import Head from 'next/head';
import { useLocale } from '@/hooks/useLocale';
import type { SeoMetadataInfo } from '@/features/common';

export type SeoMetaDataProps = {
  categoryImageUrl: string;
  seoMetaData: SeoMetadataInfo;
};

const isEmptySeoMetadata = (metadata: SeoMetadataInfo): boolean => {
  return Object.values(metadata).every((value) => value === null || value === '' || value === true);
};

export const SeoMetaData = ({ categoryImageUrl, seoMetaData }: SeoMetaDataProps) => {
  const locale = useLocale();

  if (isEmptySeoMetadata(seoMetaData)) {
    return null;
  }

  return (
    <Head>
      <title key="title">{seoMetaData.pageTitle}</title>
      {/* Open Graph Meta Tags */}
      {seoMetaData.pageTitle ? (
        <meta key="metaTitle" name="og:title" content={seoMetaData.pageTitle} />
      ) : null}

      {seoMetaData.indexEnabledFlag ? (
        <>
          {seoMetaData.currentPageUrl ? (
            <meta key="metaUrl" property="og:url" content={seoMetaData.currentPageUrl} />
          ) : null}
          {seoMetaData.pageContentDescription ? (
            <meta
              key="metaDescription"
              name="description"
              content={seoMetaData.pageContentDescription}
            />
          ) : null}
          {categoryImageUrl ? (
            <meta key="metaImage" property="og:image" content={categoryImageUrl} />
          ) : null}
          {seoMetaData.siteName ? (
            <meta key="metaSiteName" property="og:site_name" content={seoMetaData.siteName} />
          ) : null}
        </>
      ) : (
        <meta key="metaRobots" name="robots" content="noindex, follow" />
      )}

      {seoMetaData.robotsContent ? (
        <meta key="metaRobots" name="robots" content={seoMetaData.robotsContent} />
      ) : null}

      {/* Canonical URL Meta Tags */}
      {seoMetaData.canonicalUrl ? (
        <>
          <link key="linkCanonical" rel="canonical" href={seoMetaData.canonicalUrl} />
          <link
            key="linkAlternateLocale"
            rel="alternate"
            hrefLang={locale.toLowerCase()}
            href={seoMetaData.canonicalUrl}
          />
          {seoMetaData.alternateUrl && seoMetaData.langaugeLocale ? (
            <link
              key="linkAlternateObj"
              rel="alternate"
              hrefLang={seoMetaData.langaugeLocale}
              href={seoMetaData.alternateUrl}
            />
          ) : null}
        </>
      ) : null}
    </Head>
  );
};
